<template>
  <div>
    <p
      v-if="showInstructions"
      class="px-4 pt-4 pb-1 text-center blue-grey--text text--darken-3 subtitle-2"
    >
      {{ $t('common.instructions') }}
    </p>

    <v-btn
      class="ms-1 font-size-12"
      color="blue"
      small
      text
      rounded
      style="z-index: 1"
      :style="
        showClearAllFilters ? 'visibility: visible' : 'visibility: hidden'
      "
      @click.stop="clearAllFilters"
    >
      <v-icon size="16" class="me-2">mdi-close</v-icon>
      {{ $t('common.clear_filters') }}
    </v-btn>
    <FilterHeader
      :name="$t('common.content_type')"
      :isActive="showFilterByType.types"
      @change="showFilterByType.types = !showFilterByType.types"
    />
    <v-list v-if="showFilterByType.types" class="pa-0 ma-0" dense color="white">
      <v-btn
        rounded
        text
        small
        color="blue"
        class="my-1 font-size-12 ms-1"
        :style="isTypeSelected ? 'visibility: visible' : 'visibility: hidden'"
        @click="clearFilter([], ['types'])"
      >
        {{ $t('common.clear') }}
      </v-btn>
      <v-list-item
        v-for="item in typeOptions"
        dense
        class="ma-0 pa-0 px-5"
        :key="item.id"
      >
        <div
          class="d-flex justify-space-between align-center"
          style="width: 100%;"
        >
          <div class="flex-grow-1">
            <v-checkbox
              hide-details
              class="ma-0 pa-0"
              :input-value="selectedFilters.types"
              :value="item.id"
              @change="$emit('change', $event, 'types')"
            >
              <template v-slot:label>
                <p
                  class="ms-2 font-size-13 font-weight-500 blue-grey--text text--darken-3 ma-0 pa-0 align-center"
                >
                  {{ item.text }}
                </p>
              </template>
            </v-checkbox>
          </div>
          <v-btn
            v-if="hasColumnSelector"
            icon
            x-small
            @click="$emit('toggleColumnSelector', item.id)"
          >
            <v-icon color="blue-grey darken-1" size="24"
              >mdi-chevron-right</v-icon
            >
          </v-btn>
        </div>
      </v-list-item>
    </v-list>
    <FilterHeader
      class="mt-4"
      :name="$t('common.status')"
      :isActive="showFilterByType.status"
      @change="showFilterByType.status = !showFilterByType.status"
    />
    <v-list
      v-if="showFilterByType.status"
      dense
      class="pa-0 ma-0"
      color="white"
    >
      <v-btn
        rounded
        text
        small
        color="blue"
        class="my-1 font-size-12 ms-1"
        :style="isStatusSelected ? 'visibility: visible' : 'visibility: hidden'"
        @click="clearFilter([], ['status'])"
      >
        {{ $t('common.clear') }}
      </v-btn>
      <v-list-item
        v-for="item in statusOptions"
        dense
        class="ma-0 pa-0 px-5"
        :key="item.id"
      >
        <div style="width: 100%">
          <v-checkbox
            hide-details
            class="ma-0 pa-0"
            :input-value="selectedFilters.status"
            :value="item.id"
            @change="$emit('change', $event, 'status')"
          >
            <template v-slot:label>
              <p
                class="ms-2 font-size-13 font-weight-500 blue-grey--text text--darken-3 ma-0 pa-0 align-center"
              >
                {{ item.text }}
              </p>
            </template>
          </v-checkbox>
        </div>
      </v-list-item>
    </v-list>
    <FilterHeader
      class="mt-4"
      :name="$t('common.validation')"
      :isActive="showFilterByType.validation"
      @change="showFilterByType.validation = !showFilterByType.validation"
    />
    <div v-if="showFilterByType.validation">
      <v-btn
        rounded
        text
        small
        color="blue"
        class="my-1 font-size-12 ms-1"
        :style="
          Boolean(selectedValidationOption)
            ? 'visibility: visible'
            : 'visibility: hidden'
        "
        @click="clearValidationFilter"
      >
        {{ $t('common.clear') }}
      </v-btn>
      <RadioFilter
        class="px-4"
        :options="validationRadioOptions"
        :value="selectedValidationOption"
        @change="handleValidationOptionChange"
      />
    </div>
    <FilterHeader
      class="mt-4"
      :name="$t('common.location_and_tags')"
      :isActive="showFilterByType.location"
      @change="showFilterByType.location = !showFilterByType.location"
    />
    <template v-if="showFilterByType.location">
      <v-btn
        rounded
        text
        small
        color="blue"
        class="my-1 font-size-12 ms-1"
        :style="
          isLocationSelected ? 'visibility: visible' : 'visibility: hidden'
        "
        @click="
          clearFilter(
            [],
            ['sites', 'siteGroups', 'tags', 'tagGroups', 'countryCodes']
          )
        "
      >
        {{ $t('common.clear') }}
      </v-btn>
      <div class="mx-5">
        <MultipleSelect
          persistent-hint
          outlined
          dense
          hide-details
          item-value="id"
          class="mb-4"
          :loading="isLoading"
          :value="selectedFilters.sites"
          :options="sites"
          :label="$t('common.location_tags_options.site')"
          @change="$emit('change', $event, 'sites')"
        />
        <MultipleSelect
          clearable
          outlined
          dense
          item-value="id"
          hide-details
          class="mb-4"
          :loading="isLoading"
          :value="selectedFilters.siteGroups"
          :options="siteGroups"
          :label="$t('common.location_tags_options.site_group')"
          @change="$emit('change', $event, 'siteGroups')"
        />

        <MultipleSelect
          clearable
          outlined
          dense
          item-value="id"
          hide-details
          class="mb-4"
          :loading="isLoading"
          :value="selectedFilters.countryCodes"
          :options="countryCodeOptions"
          :label="$t('common.location_tags_options.country')"
          @change="$emit('change', $event, 'countryCodes')"
        />

        <MultipleSelect
          clearable
          outlined
          dense
          item-value="id"
          hide-details
          class="mb-4"
          :loading="isLoading"
          :value="selectedFilters.tags"
          :options="tags"
          :label="$t('common.location_tags_options.tags')"
          @change="$emit('change', $event, 'tags')"
        />

        <MultipleSelect
          clearable
          outlined
          dense
          item-value="id"
          hide-details
          :loading="isLoading"
          :value="selectedFilters.tagGroups"
          :options="tagGroups"
          :label="$t('common.location_tags_options.tag_groups')"
          @change="$emit('change', $event, 'tagGroups')"
        />
      </div>
    </template>
    <FilterHeader
      class="mt-4"
      :name="$t('common.other')"
      :isActive="showFilterByType.other"
      @change="showFilterByType.other = !showFilterByType.other"
    />
    <template v-if="showFilterByType.other">
      <v-btn
        rounded
        text
        small
        color="blue"
        class="my-1 font-size-12 ms-1"
        :style="isOtherSelected ? 'visibility: visible' : 'visibility: hidden'"
        @click="clearFilter([], ['categories', 'assignees'])"
      >
        {{ $t('common.clear') }}
      </v-btn>
      <div class="mx-5">
        <MultipleSelect
          clearable
          outlined
          dense
          item-value="value"
          hide-details
          class="mb-4"
          :value="selectedFilters.categories"
          :options="categories"
          :label="$t('common.other_options.categories')"
          @change="$emit('change', $event, 'categories')"
        />

        <AssigneesSelector
          multiple
          hideDetails
          :selectedAccountIds="selectedFilters.assignees"
          :availableAccounts="assignees"
          :isLoading="isSearching || isLoading"
          :disabled="isLoading"
          :label="$t('common.other_options.assignees')"
          @change="$emit('change', $event, 'assignees')"
          @search="handleSearch"
        />
      </div>
    </template>
    <FilterHeader
      class="mt-4"
      :name="$t('common.date')"
      :isActive="showFilterByType.date"
      :hasFilterSelected="isDateSelected"
      @change="showFilterByType.date = !showFilterByType.date"
    />
    <template v-if="showFilterByType.date">
      <v-btn
        rounded
        text
        small
        color="blue"
        class="my-1 font-size-12 ms-1"
        :style="isDateSelected ? 'visibility: visible' : 'visibility: hidden'"
        @click="clearDateFilter(['createdAt', 'dueDate'])"
      >
        {{ $t('common.clear') }}
      </v-btn>
      <div class="mx-5">
        <CreationDateSelector
          class="mb-4"
          :selectedFilters="selectedFilters"
          @change="handleDateChange"
        />
        <DueDateSelector
          :selectedFilters="selectedFilters"
          @change="handleDateChange"
        />
      </div>
    </template>
  </div>
</template>

<script>
import store from '@/store/index.js'
import _ from 'lodash'
import constants from './constants'
import api from '@/api'

import AssigneesSelector from '@/ui/shared/components/AssigneeSelector.vue'
import MultipleSelect from '@/ui/shared/components/MultipleSelect.vue'
import DueDateSelector from '@/ui/shared/components/DueDateSelector.vue'
import CreationDateSelector from '@/ui/shared/components/CreationDateSelector.vue'
import FilterHeader from './FilterHeader'
import RadioFilter from '@/ui/shared/components/RadioFilter'

export default {
  name: 'Filters',

  components: {
    MultipleSelect,
    AssigneesSelector,
    DueDateSelector,
    CreationDateSelector,
    FilterHeader,
    RadioFilter,
  },

  props: {
    showInstructions: { type: Boolean, required: false },
    hasColumnSelector: { type: Boolean, required: false },
    selectedFilters: {
      type: Object,
      required: true,
    },
    typeOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      showFilterByType: {
        types: true,
        status: true,
        validation: true,
        location: true,
        other: true,
        date: true,
      },
      isLoading: false,
      isSearching: false,
      categories: [],
      assignees: [],
      statusOptions: [
        {
          id: 'open',
          text: this.$t('common.status_options.open'),
        },
        {
          id: 'inProgress',
          text: this.$t('common.status_options.in_progress'),
        },
        {
          id: 'completed',
          text: this.$t('common.status_options.completed'),
        },
        {
          id: 'onHold',
          text: this.$t('common.status_options.on_hold'),
        },
        {
          id: 'canceled',
          text: this.$t('common.status_options.canceled'),
        },
      ],
      sites: [],
      siteGroups: [],
      tags: [],
      tagGroups: [],
      countryCodes: [],
      selectedValidationOption: this.getInitialValidationOption(), // This variable stores the UI option for the validation type, which will be mapped to the corresponding values of “hasImplementationValidation” and “hasImpactValidation” filters later.
      validationOptions: constants.getValidationOptions(),
      validationRadioOptions: Object.values(constants.getValidationOptions()),
    }
  },

  mounted() {
    this.setStructure()
  },

  computed: {
    organizationId() {
      return store.access.getOrganizationId()
    },

    showClearAllFilters() {
      const filterValues = Object.values(this.selectedFilters)
      return Boolean(_.compact(filterValues.flatMap(value => value))?.length)
    },

    isTypeSelected() {
      return Boolean(this.selectedFilters?.types?.length)
    },

    isStatusSelected() {
      return Boolean(this.selectedFilters?.status?.length)
    },

    isLocationSelected() {
      return Boolean(
        this.selectedFilters?.sites?.length ||
          this.selectedFilters?.siteGroups?.length ||
          this.selectedFilters?.countryCodes?.length ||
          this.selectedFilters?.tags?.length ||
          this.selectedFilters?.tagGroups?.length
      )
    },

    isOtherSelected() {
      return Boolean(
        this.selectedFilters?.categories?.length ||
          this.selectedFilters?.assignees?.length
      )
    },

    isDateSelected() {
      return Boolean(
        this.selectedFilters?.createdAt?.length ||
          this.selectedFilters?.dueDate?.length
      )
    },

    countryCodeOptions() {
      return (
        this.countryCodes.map?.(countryCode => {
          return {
            id: countryCode,
            name: this.$t(`countries.${countryCode}`),
          }
        }) ?? []
      )
    },
  },

  methods: {
    setStructure() {
      try {
        this.isLoading = true
        const organizationStructure = store.organization.getOrganizationStructure()
        const countryCodes = store.organization.getCountyCodes() ?? []
        const organizationCategories = store.organization.getOrganizationCategories()
        this.sites = organizationStructure?.sites ?? []
        this.siteGroups = organizationStructure?.siteGroups ?? []
        this.tags = organizationStructure?.tags ?? []
        this.tagGroups = organizationStructure?.tagGroups ?? []
        this.countryCodes = countryCodes
        this.categories = organizationCategories.map(category => {
          return {
            ...category,
            name: this.$t(`categories.${category.name}`),
            value: category.name,
          }
        })
        this.fetchAccounts()
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },

    getInitialValidationOption() {
      // Setting the options to true or false explicitly to avoid selecting them by mistake when a null value is received
      if (
        this.selectedFilters.hasImplementationValidation &&
        this.selectedFilters.hasFinancialValidation
      ) {
        return this.validationOptions.impactValidated.id
      }

      if (
        this.selectedFilters.hasImplementationValidation &&
        this.selectedFilters.hasFinancialValidation === false
      ) {
        return this.validationOptions.implemented.id
      }

      if (
        this.selectedFilters.hasImplementationValidation === false &&
        this.selectedFilters.hasFinancialValidation === false
      ) {
        return this.validationOptions.notImplemented.id
      }
    },

    handleValidationOptionChange(value) {
      this.selectedValidationOption = value

      switch (this.selectedValidationOption) {
        case this.validationOptions.impactValidated.id:
          this.$emit('change', true, 'hasImplementationValidation')
          this.$emit('change', true, 'hasFinancialValidation')
          break
        case this.validationOptions.implemented.id:
        case this.validationOptions.impactNotValidated.id:
          this.$emit('change', true, 'hasImplementationValidation')
          this.$emit('change', false, 'hasFinancialValidation')
          break
        case this.validationOptions.notImplemented.id:
          this.$emit('change', false, 'hasImplementationValidation')
          this.$emit('change', false, 'hasFinancialValidation')
          break
        default:
          null
      }
    },

    clearDateFilter(keys) {
      _.forEach(['dueDateOption', 'createdAtOption'], key => {
        this.$emit('change', null, key)
      })
      this.clearFilter([], keys)
    },

    clearValidationFilter() {
      this.selectedValidationOption = null

      this.clearFilter(null, [
        'hasImplementationValidation',
        'hasFinancialValidation',
      ])
    },

    clearFilter(value, filters) {
      this.$track('msOffice.clearFilters', {
        env: window.Office.context.host,
        filtersCleared: filters,
      })

      _.forEach(filters, filter => {
        this.$emit('change', value, filter)
      })
    },

    clearAllFilters() {
      this.$track('msOffice.clearAllFilters', {
        env: window.Office.context.host,
      })

      const selectedFiltersKeys = Object.keys(this.selectedFilters)

      if (
        selectedFiltersKeys.includes('hasImplementationValidation') ||
        selectedFiltersKeys.includes('hasFinancialValidation')
      ) {
        this.selectedValidationOption = null
      }

      selectedFiltersKeys?.map(key => {
        const isArray = _.isArray(this.selectedFilters[key])
        this.$emit('change', isArray ? [] : null, key)
      })
    },

    async fetchAccounts(terms = '') {
      try {
        this.isSearching = true
        const { data } = await api.stream.getAccounts(this.organizationId, {
          terms,
        })
        this.assignees = data?.results ?? []
      } catch (error) {
        console.log(error)
      } finally {
        this.isSearching = false
      }
    },

    handleDateChange(values, key) {
      this.$emit('change', values, key)
    },

    handleSearch(terms) {
      this.fetchAccounts(terms)
    },
  },
}
</script>
